import {
  createSignal,
  createEffect,
  createMemo,
  onMount,
  onCleanup,
  Show,
  untrack,
} from "solid-js";
import { persistantStore } from "../../../../../store/persistantStore";
import { apiEndpoints } from "@config/env";
import { getData, setupMediaContainerScaling } from "../../../../../utils/global";
import { logPageLoad, logActivity, getCurrentProjectDatasetIds } from "../../../../../utils/logging";
import { debug as logDebug, error as logError } from "../../../../../utils/console";
import Loading from "../../../../../components/utilities/Loading";
import DropDownButton from "../../../../../components/buttons/DropDownButton";
import { useDashboardShareRestore } from "../../../../../hooks/useDashboardShareRestore";
import { buildPerformanceDriversShareState, applyPerformanceDriversShareState } from "../../../../../utils/reportShareState";
import PerformanceDriversChannelTable from "../../../../../components/performanceDrivers/PerformanceDriversChannelTable";
import PerformanceDriversImportanceTable from "../../../../../components/performanceDrivers/PerformanceDriversImportanceTable";
import PerformanceDriversManeuversTable from "../../../../../components/performanceDrivers/PerformanceDriversManeuversTable";
import PerformanceDriversBeeswarmSection from "../../../../../components/performanceDrivers/PerformanceDriversBeeswarmSection";
import PerformanceDriversModelFit from "../../../../../components/charts/PerformanceDriversModelFit";
import {
  availablePerformanceDriversConfigurations,
  buildDriversTableRows,
  needsPerformanceDriversSnapshotRebuild,
  performanceDriversTableTwsBins,
  POINT_OF_SAIL_OPTIONS,
  resolvePerformanceDriversTargetUnit,
  tableColumnsForPointOfSail,
  importanceTableColumnsForPointOfSail,
  type PerformanceDriversEnvelopeSlice,
  type PointOfSail,
} from "../../../../../utils/performanceDriversModelFit";
import {
  fetchPerformanceDriversManeuverTable,
  type PerformanceDriversManeuverRow,
} from "../../../../../utils/performanceDriversManeuvers";
import {
  fetchPerformanceDriversStraightLineTable,
  envelopeHasEmbeddedStraightLine,
  mergeStraightLineIntoTableRows,
  type PerformanceDriversStraightLineRow,
} from "../../../../../utils/performanceDriversStraightLine";
import {
  gp50PerformanceDriversManeuverLabel,
  gp50PerformanceDriversManeuverType,
} from "../../../../../utils/gp50ManeuverCheatSheetColumns";
import {
  performanceDriversExplanation,
  performanceDriversDateRangeLabel,
  performanceDriversModelValidationExplanation,
  performanceDriversNoSnapshotMessage,
  performanceDriversStaleSnapshotMessage,
  performanceDriversTitle,
} from "../../../../../utils/performanceDriversExplanation";
import {
  beeswarmSectionTitle,
  effectiveBeeswarmPointOfSail,
  fetchPerformanceDriversEnvelopeObject,
  findPerformanceDriversCell,
  isPerformanceDriversTwsAll,
  performanceDriversObjectNameForKind,
  performanceDriversTwsDropdownOptions,
  resolvePerformanceDriversEnvelope,
  resolveManeuverBeeswarmConfiguration,
  PERFORMANCE_DRIVERS_TWS_ALL,
  type PerformanceDriversBeeswarmEnvelope,
  type PerformanceDriversBeeswarmKind,
  type PerformanceDriversTwsSelection,
} from "../../../../../utils/performanceDriversBeeswarm";

const { selectedClassName, selectedProjectId } = persistantStore;

const OBJECT_NAME = "performance_drivers_vmg_shap";

function legFromDisplay(item: string): PointOfSail {
  if (item === "DOWNWIND") return "DOWNWIND";
  if (item === "REACHING") return "REACHING";
  return "UPWIND";
}

function isPointOfSail(value: string): value is PointOfSail {
  return POINT_OF_SAIL_OPTIONS.includes(value as PointOfSail);
}

function twsSelectionFromDropdown(item: string): PerformanceDriversTwsSelection {
  if (item === PERFORMANCE_DRIVERS_TWS_ALL) return PERFORMANCE_DRIVERS_TWS_ALL;
  const parsed = Number(item);
  return Number.isFinite(parsed) ? parsed : PERFORMANCE_DRIVERS_TWS_ALL;
}

function twsDropdownLabel(selection: PerformanceDriversTwsSelection): string {
  return isPerformanceDriversTwsAll(selection) ? PERFORMANCE_DRIVERS_TWS_ALL : String(selection);
}

export default function PerformanceDriversPage() {
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<string | null>(null);
  const [envelope, setEnvelope] = createSignal<PerformanceDriversEnvelopeSlice | null>(null);
  const [configuration, setConfiguration] = createSignal<string>("");
  const [pointOfSail, setPointOfSail] = createSignal<PointOfSail>("UPWIND");
  const [twsSelection, setTwsSelection] = createSignal<PerformanceDriversTwsSelection>(
    PERFORMANCE_DRIVERS_TWS_ALL
  );
  const [hideDominantDrivers, setHideDominantDrivers] = createSignal(false);
  const [loadingManeuvers, setLoadingManeuvers] = createSignal(false);
  const [errorManeuvers, setErrorManeuvers] = createSignal<string | null>(null);
  const [maneuverRows, setManeuverRows] = createSignal<PerformanceDriversManeuverRow[]>([]);
  const [loadingStraightLine, setLoadingStraightLine] = createSignal(false);
  const [errorStraightLine, setErrorStraightLine] = createSignal<string | null>(null);
  const [straightLineRows, setStraightLineRows] = createSignal<PerformanceDriversStraightLineRow[]>([]);
  const [dynamicsEnvelope, setDynamicsEnvelope] =
    createSignal<PerformanceDriversBeeswarmEnvelope | null>(null);
  const [maneuverEnvelope, setManeuverEnvelope] =
    createSignal<PerformanceDriversBeeswarmEnvelope | null>(null);
  const [loadingDynamics, setLoadingDynamics] = createSignal(false);
  const [loadingManeuverBeeswarm, setLoadingManeuverBeeswarm] = createSignal(false);
  const [errorDynamics, setErrorDynamics] = createSignal<string | null>(null);
  const [errorManeuverBeeswarm, setErrorManeuverBeeswarm] = createSignal<string | null>(null);

  let fetchSeq = 0;
  let dynamicsFetchSeq = 0;
  let maneuverBeeswarmFetchSeq = 0;

  const PERFORMANCE_DRIVERS_PAGE_KEY = "PerformanceDrivers";
  useDashboardShareRestore(
    PERFORMANCE_DRIVERS_PAGE_KEY,
    () =>
      buildPerformanceDriversShareState({
        configuration: configuration(),
        pointOfSail: pointOfSail(),
        twsSelection: twsSelection(),
      }),
    (obj) =>
      applyPerformanceDriversShareState(obj, PERFORMANCE_DRIVERS_PAGE_KEY, {
        setConfiguration,
        setPointOfSail: (value) => {
          if (typeof value === "string" && isPointOfSail(value)) {
            setPointOfSail(value);
          }
        },
        setTwsSelection: (value) => {
          if (value === PERFORMANCE_DRIVERS_TWS_ALL) {
            setTwsSelection(PERFORMANCE_DRIVERS_TWS_ALL);
          } else if (typeof value === "number" && Number.isFinite(value)) {
            setTwsSelection(value);
          }
        },
      })
  );

  const schemaVersion = createMemo(() => envelope()?.schema_version ?? 1);
  const showConfigSelector = createMemo(
    () => schemaVersion() >= 2 && availableConfigurations().length > 0
  );
  const targetUnit = createMemo(() => resolvePerformanceDriversTargetUnit(envelope(), pointOfSail()));
  const showBeeswarmView = createMemo(() => !isPerformanceDriversTwsAll(twsSelection()));
  const selectedTwsBin = createMemo(() =>
    isPerformanceDriversTwsAll(twsSelection()) ? null : twsSelection()
  );

  const pointOfSailOptions = createMemo((): PointOfSail[] => {
    const fromEnvelope = envelope()?.point_of_sail_options ?? [];
    const filtered = fromEnvelope.filter(isPointOfSail);
    return filtered.length > 0 ? filtered : [...POINT_OF_SAIL_OPTIONS];
  });

  const availableConfigurations = createMemo(() =>
    availablePerformanceDriversConfigurations(envelope(), pointOfSail(), schemaVersion())
  );

  const twsDropdownOptions = createMemo(() =>
    performanceDriversTwsDropdownOptions(envelope()).map(String)
  );

  const tableColumns = createMemo(() => tableColumnsForPointOfSail(envelope(), pointOfSail()));
  const importanceTableColumns = createMemo(() =>
    importanceTableColumnsForPointOfSail(envelope(), pointOfSail())
  );

  const tableRows = createMemo(() =>
    buildDriversTableRows(
      envelope(),
      showConfigSelector() ? configuration() : undefined,
      pointOfSail(),
      schemaVersion(),
      showConfigSelector()
    )
  );

  const channelTableRows = createMemo(() => {
    const rows = tableRows();
    if (envelopeHasEmbeddedStraightLine(envelope())) {
      return rows;
    }
    return mergeStraightLineIntoTableRows(rows, straightLineRows(), tableColumns());
  });

  const maneuverTwsBins = createMemo(() => performanceDriversTableTwsBins(envelope()));

  const hasTrainedRows = createMemo(() => tableRows().some((row) => !row.skipped));

  const snapshotNeedsRebuild = createMemo(() => needsPerformanceDriversSnapshotRebuild(envelope()));

  const maneuverType = createMemo(() => gp50PerformanceDriversManeuverType(pointOfSail()));
  const showManeuverDrivers = createMemo(() => maneuverType() != null);
  const maneuverTypeLabel = createMemo(() => gp50PerformanceDriversManeuverLabel(pointOfSail()));

  const beeswarmCellParams = createMemo(() => ({
    configuration: showConfigSelector() ? configuration() : undefined,
    twsBin: selectedTwsBin() ?? 0,
    pointOfSail: pointOfSail(),
    schemaVersion: schemaVersion(),
    showConfigSelector: showConfigSelector(),
  }));

  const staticBeeswarmCell = createMemo(() =>
    findPerformanceDriversCell({
      envelope: envelope(),
      ...beeswarmCellParams(),
      pointOfSail: effectiveBeeswarmPointOfSail("static", pointOfSail()),
    })
  );

  const dynamicsBeeswarmCell = createMemo(() =>
    findPerformanceDriversCell({
      envelope: dynamicsEnvelope(),
      ...beeswarmCellParams(),
      pointOfSail: effectiveBeeswarmPointOfSail("dynamics", pointOfSail()),
    })
  );

  const maneuverBeeswarmCell = createMemo(() => {
    const mEnv = maneuverEnvelope();
    if (!mEnv || selectedTwsBin() == null) return null;

    const mSchema = mEnv.schema_version ?? schemaVersion();
    const maneuverConfig = resolveManeuverBeeswarmConfiguration({
      maneuverEnvelope: mEnv,
      preferredConfiguration: configuration(),
      schemaVersion: mSchema,
    });

    return findPerformanceDriversCell({
      envelope: mEnv,
      configuration: maneuverConfig.configuration,
      twsBin: selectedTwsBin() ?? 0,
      pointOfSail: effectiveBeeswarmPointOfSail("maneuver", pointOfSail()),
      schemaVersion: mSchema,
      showConfigSelector: maneuverConfig.showConfigSelector,
    });
  });

  const fetchEnvelope = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const seq = ++fetchSeq;
    if (!className || !projectId) {
      setEnvelope(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setDynamicsEnvelope(null);
    setManeuverEnvelope(null);
    setErrorDynamics(null);
    setErrorManeuverBeeswarm(null);
    try {
      const url = `${apiEndpoints.app.projects}/object/latest?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&object_name=${encodeURIComponent(OBJECT_NAME)}`;
      const result = await getData(url);
      if (seq !== fetchSeq) return;
      if (!result?.success || !result.data) {
        setEnvelope(null);
        setError(performanceDriversNoSnapshotMessage());
        return;
      }
      const env = resolvePerformanceDriversEnvelope(result.data);
      if (!env) {
        setEnvelope(null);
        setError(performanceDriversNoSnapshotMessage());
        return;
      }
      setEnvelope(env);

      const posOptions = (env.point_of_sail_options ?? POINT_OF_SAIL_OPTIONS).filter(isPointOfSail);
      const nextPos = posOptions.includes(pointOfSail()) ? pointOfSail() : posOptions[0] ?? "UPWIND";
      setPointOfSail(nextPos);

      const configs = availablePerformanceDriversConfigurations(env, nextPos, env.schema_version ?? 1);
      if (env.schema_version >= 2 && configs.length > 0) {
        setConfiguration((prev) => (prev && configs.includes(prev) ? prev : configs[0]));
      } else {
        setConfiguration("");
      }

      logDebug("PerformanceDrivers: loaded envelope", {
        schema: env.schema_version,
        cells: env.condition_cells?.length,
      });
    } catch (err) {
      if (seq !== fetchSeq) return;
      logError("PerformanceDrivers: fetch failed", err);
      setError(err instanceof Error ? err.message : "Failed to load performance drivers");
      setEnvelope(null);
    } finally {
      if (seq === fetchSeq) setLoading(false);
    }
  };

  const fetchDynamicsEnvelope = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId || !showBeeswarmView()) return;

    const seq = ++dynamicsFetchSeq;
    setLoadingDynamics(true);
    setErrorDynamics(null);
    try {
      const data = await fetchPerformanceDriversEnvelopeObject({
        className,
        projectId: String(projectId),
        objectName: performanceDriversObjectNameForKind("dynamics"),
      });
      if (seq !== dynamicsFetchSeq) return;
      if (!data) {
        setDynamicsEnvelope(null);
        setErrorDynamics(
          "No dynamics SHAP snapshot found. Run condition_model.py with kind dynamics or all."
        );
        return;
      }
      setDynamicsEnvelope(data);
    } catch (err) {
      if (seq !== dynamicsFetchSeq) return;
      logError("PerformanceDrivers: dynamics fetch failed", err);
      setErrorDynamics(err instanceof Error ? err.message : "Failed to load dynamics drivers");
      setDynamicsEnvelope(null);
    } finally {
      if (seq === dynamicsFetchSeq) setLoadingDynamics(false);
    }
  };

  const fetchManeuverBeeswarmEnvelope = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const type = maneuverType();
    if (!className || !projectId || !showBeeswarmView() || !type) {
      setManeuverEnvelope(null);
      setErrorManeuverBeeswarm(null);
      setLoadingManeuverBeeswarm(false);
      return;
    }

    const seq = ++maneuverBeeswarmFetchSeq;
    setLoadingManeuverBeeswarm(true);
    setErrorManeuverBeeswarm(null);
    try {
      const data = await fetchPerformanceDriversEnvelopeObject({
        className,
        projectId: String(projectId),
        objectName: performanceDriversObjectNameForKind("maneuver", type),
      });
      if (seq !== maneuverBeeswarmFetchSeq) return;
      if (!data) {
        setManeuverEnvelope(null);
        setErrorManeuverBeeswarm(
          `No ${maneuverTypeLabel()} SHAP snapshot found. Run condition_model.py with kind maneuvers or all.`
        );
        return;
      }
      setManeuverEnvelope(data);
    } catch (err) {
      if (seq !== maneuverBeeswarmFetchSeq) return;
      logError("PerformanceDrivers: maneuver beeswarm fetch failed", err);
      setErrorManeuverBeeswarm(
        err instanceof Error ? err.message : "Failed to load maneuver drivers"
      );
      setManeuverEnvelope(null);
    } finally {
      if (seq === maneuverBeeswarmFetchSeq) setLoadingManeuverBeeswarm(false);
    }
  };

  createEffect(() => {
    const configs = availableConfigurations();
    if (showConfigSelector() && configs.length > 0 && !configs.includes(configuration())) {
      setConfiguration(configs[0]);
    }
  });

  createEffect(() => {
    selectedClassName();
    selectedProjectId();
    untrack(() => void fetchEnvelope());
  });

  createEffect(() => {
    if (!showBeeswarmView()) return;
    selectedClassName();
    selectedProjectId();
    untrack(() => void fetchDynamicsEnvelope());
  });

  createEffect(() => {
    if (!showBeeswarmView()) return;
    selectedClassName();
    selectedProjectId();
    pointOfSail();
    untrack(() => void fetchManeuverBeeswarmEnvelope());
  });

  createEffect(() => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const env = envelope();
    const twsBins = maneuverTwsBins();
    const pos = pointOfSail();
    const configGroup = showConfigSelector() ? configuration() : undefined;

    if (!className || !projectId || !env || twsBins.length === 0 || showBeeswarmView()) {
      setStraightLineRows([]);
      setErrorStraightLine(null);
      setLoadingStraightLine(false);
      return;
    }

    if (envelopeHasEmbeddedStraightLine(env)) {
      setStraightLineRows([]);
      setErrorStraightLine(null);
      setLoadingStraightLine(false);
      return;
    }

    let cancelled = false;
    setLoadingStraightLine(true);
    setErrorStraightLine(null);

    void fetchPerformanceDriversStraightLineTable({
      className,
      projectId: String(projectId),
      pointOfSail: pos,
      twsBins,
      configurationGroup: configGroup,
    })
      .then((rows) => {
        if (!cancelled) setStraightLineRows(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          logError("PerformanceDrivers: straight line fetch failed", err);
          setErrorStraightLine(err instanceof Error ? err.message : "Failed to load straight line data");
          setStraightLineRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingStraightLine(false);
      });

    onCleanup(() => {
      cancelled = true;
    });
  });

  createEffect(() => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const env = envelope();
    const twsBins = maneuverTwsBins();
    const type = maneuverType();
    const configGroup = showConfigSelector() ? configuration() : undefined;

    if (
      !className ||
      !projectId ||
      !env ||
      twsBins.length === 0 ||
      showBeeswarmView() ||
      !type
    ) {
      setManeuverRows([]);
      setErrorManeuvers(null);
      setLoadingManeuvers(false);
      return;
    }

    let cancelled = false;
    setLoadingManeuvers(true);
    setErrorManeuvers(null);

    void fetchPerformanceDriversManeuverTable({
      className,
      projectId: String(projectId),
      maneuverType: type,
      twsBins,
      configurationGroup: configGroup,
    })
      .then((rows) => {
        if (!cancelled) setManeuverRows(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          logError("PerformanceDrivers: maneuver fetch failed", err);
          setErrorManeuvers(err instanceof Error ? err.message : "Failed to load maneuver data");
          setManeuverRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingManeuvers(false);
      });

    onCleanup(() => {
      cancelled = true;
    });
  });

  onMount(() => {
    logPageLoad("PerformanceDrivers.tsx", "Performance Drivers", "Loaded");
    const cleanupScaling = setupMediaContainerScaling({
      logPrefix: "PerformanceDrivers",
      scaleToWidth: true,
    });
    onCleanup(() => cleanupScaling());
  });

  const dateRangeLabel = createMemo(() =>
    performanceDriversDateRangeLabel(
      (envelope() as { date_range?: { start: string; end: string } } | null)?.date_range
    )
  );

  const beeswarmTitleForKind = (kind: PerformanceDriversBeeswarmKind) => {
    const tws = selectedTwsBin();
    if (tws == null) return "";
    return beeswarmSectionTitle(
      kind,
      tws,
      pointOfSail(),
      kind === "maneuver" ? maneuverTypeLabel() : undefined
    );
  };

  return (
    <div id="media-container" class="cheat-sheet-page performance-drivers-page">
      <div class="container relative">
        <div class="performance-charts-scroll-container">
          <div class="performance-charts-scroll-inner cheat-sheet-scroll-inner performance-drivers-scroll-inner">
            <div class="performance-drivers-page-header">
              <h2 class="report-page-title cheat-sheet-title performance-drivers-page-title">
                {performanceDriversTitle()}
              </h2>
              <Show when={dateRangeLabel()}>
                <span class="performance-drivers-date-range">{dateRangeLabel()}</span>
              </Show>
            </div>

            <p class="cheat-sheet-table-explanation performance-drivers-explanation">
              {performanceDriversExplanation(schemaVersion())}
            </p>

            <div class="race-summary-dropdown-section cheat-sheet-dropdowns performance-drivers-dropdowns">
              <Show when={showConfigSelector()}>
                <DropDownButton
                  options={availableConfigurations()}
                  defaultText={configuration() || "Config group"}
                  smallLabel="Config group"
                  size="big"
                  handleSelection={async (item) => {
                    setConfiguration(item);
                    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                    await logActivity(
                      project_id,
                      dataset_id,
                      "PerformanceDrivers.tsx",
                      "Performance Drivers",
                      `Configuration ${item}`
                    );
                  }}
                />
              </Show>
              <DropDownButton
                options={[...pointOfSailOptions()]}
                defaultText={pointOfSail()}
                smallLabel="Point of sail"
                size="big"
                handleSelection={async (item) => {
                  setPointOfSail(legFromDisplay(item));
                  const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                  await logActivity(
                    project_id,
                    dataset_id,
                    "PerformanceDrivers.tsx",
                    "Performance Drivers",
                    `PoS ${item}`
                  );
                }}
              />
              <DropDownButton
                options={twsDropdownOptions()}
                defaultText={twsDropdownLabel(twsSelection())}
                smallLabel="TWS"
                size="big"
                handleSelection={async (item) => {
                  setTwsSelection(twsSelectionFromDropdown(item));
                  const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                  await logActivity(
                    project_id,
                    dataset_id,
                    "PerformanceDrivers.tsx",
                    "Performance Drivers",
                    `TWS ${item}`
                  );
                }}
              />
            </div>

            <Show when={loading()}>
              <div class="cheat-sheet-table-loading-wrapper performance-drivers-loading">
                <Loading fullScreen={false} />
              </div>
            </Show>

            <Show when={!loading() && error()}>
              <div class="performance-drivers-error">{error()}</div>
            </Show>

            <Show when={!loading() && !error() && envelope()}>
              <div class="performance-drivers-main">
                <Show when={snapshotNeedsRebuild()}>
                  <p class="performance-drivers-stale-hint">{performanceDriversStaleSnapshotMessage()}</p>
                </Show>

                <Show when={!showBeeswarmView()}>
                  <PerformanceDriversChannelTable
                    columns={tableColumns()}
                    rows={channelTableRows()}
                    displayNames={envelope()?.feature_display_names}
                    pointOfSail={pointOfSail()}
                    loading={loadingStraightLine()}
                    error={errorStraightLine()}
                  />

                  <Show
                    when={hasTrainedRows()}
                    fallback={
                      <p class="performance-drivers-empty cheat-sheet-empty-message">
                        No trained models for this configuration and point of sail. Try another selection or rebuild with more data.
                      </p>
                    }
                  >
                    <PerformanceDriversImportanceTable
                      columns={importanceTableColumns()}
                      rows={tableRows()}
                      displayNames={envelope()?.feature_display_names}
                      targetUnit={targetUnit()}
                    />
                  </Show>

                  <Show when={showManeuverDrivers()}>
                    <PerformanceDriversManeuversTable
                      rows={maneuverRows()}
                      loading={loadingManeuvers()}
                      error={errorManeuvers()}
                      maneuverType={maneuverType()!}
                    />
                  </Show>
                </Show>

                <Show when={showBeeswarmView()}>
                    <div class="performance-drivers-beeswarm-stack">
                    <PerformanceDriversBeeswarmSection
                      kind="static"
                      title={beeswarmTitleForKind("static")}
                      cell={staticBeeswarmCell()}
                      displayNames={envelope()?.feature_display_names}
                      targetUnit={targetUnit()}
                      showModelFit={false}
                      hideDominantDrivers={hideDominantDrivers()}
                      onHideDominantDriversChange={async (checked) => {
                        setHideDominantDrivers(checked);
                        const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                        await logActivity(
                          project_id,
                          dataset_id,
                          "PerformanceDrivers.tsx",
                          "Performance Drivers",
                          checked ? "Highlight settings on" : "Highlight settings off"
                        );
                      }}
                    />

                    <PerformanceDriversBeeswarmSection
                      kind="dynamics"
                      title={beeswarmTitleForKind("dynamics")}
                      cell={dynamicsBeeswarmCell()}
                      displayNames={dynamicsEnvelope()?.feature_display_names}
                      targetUnit={targetUnit()}
                      showModelFit={false}
                      loading={loadingDynamics()}
                      error={errorDynamics()}
                    />

                    <Show when={showManeuverDrivers()}>
                      <PerformanceDriversBeeswarmSection
                        kind="maneuver"
                        title={beeswarmTitleForKind("maneuver")}
                        cell={maneuverBeeswarmCell()}
                        displayNames={maneuverEnvelope()?.feature_display_names}
                        targetUnit="VMG%"
                        showModelFit={false}
                        loading={loadingManeuverBeeswarm()}
                        error={errorManeuverBeeswarm()}
                        maneuverLabel={maneuverTypeLabel()}
                      />
                    </Show>
                  </div>

                  <section class="performance-drivers-table-section performance-drivers-validation-section">
                    <h3 class="performance-drivers-section-title">Model validation</h3>
                    <p class="performance-drivers-section-explanation">
                      {performanceDriversModelValidationExplanation()}
                    </p>
                    <div
                      class={`performance-drivers-fit-summary${showManeuverDrivers() ? "" : " performance-drivers-fit-summary--two-columns"}`}
                    >
                      <PerformanceDriversModelFit
                      title={beeswarmTitleForKind("static")}
                      layout="horizontal"
                      showExplanation={false}
                      fit={staticBeeswarmCell()?.fit ?? null}
                      nSamples={staticBeeswarmCell()?.n_samples ?? 0}
                      nBoats={staticBeeswarmCell()?.n_boats ?? 0}
                      skipped={staticBeeswarmCell()?.skipped}
                      skipReason={staticBeeswarmCell()?.skip_reason}
                      targetUnit={targetUnit()}
                    />
                    <PerformanceDriversModelFit
                      title={beeswarmTitleForKind("dynamics")}
                      layout="horizontal"
                      showExplanation={false}
                      fit={dynamicsBeeswarmCell()?.fit ?? null}
                      nSamples={dynamicsBeeswarmCell()?.n_samples ?? 0}
                      nBoats={dynamicsBeeswarmCell()?.n_boats ?? 0}
                      skipped={dynamicsBeeswarmCell()?.skipped}
                      skipReason={
                        errorDynamics() ??
                        dynamicsBeeswarmCell()?.skip_reason ??
                        "No dynamics model fit for this cell."
                      }
                      targetUnit={targetUnit()}
                    />
                    <Show when={showManeuverDrivers()}>
                      <PerformanceDriversModelFit
                        title={beeswarmTitleForKind("maneuver")}
                        layout="horizontal"
                        showExplanation={false}
                        fit={maneuverBeeswarmCell()?.fit ?? null}
                        nSamples={maneuverBeeswarmCell()?.n_samples ?? 0}
                        nBoats={maneuverBeeswarmCell()?.n_boats ?? 0}
                        skipped={maneuverBeeswarmCell()?.skipped}
                        skipReason={
                          errorManeuverBeeswarm() ??
                          maneuverBeeswarmCell()?.skip_reason ??
                          "No maneuver model fit for this cell."
                        }
                        targetUnit="VMG%"
                      />
                    </Show>
                    </div>
                  </section>
                </Show>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </div>
  );
}
